import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { historyService } from '../../services/history';
import { normalizeBibleHistoryPath } from '../../services/bibleHistoryPaths';
import { bibleService } from '../../services/bible';
import { t, formatDateTime } from '../../i18n/uiText';

export default function HistoryModal({ onClose }) {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [formattedTitles, setFormattedTitles] = useState({});

  useEffect(() => {
    // Блокируем скролл body при открытии модального окна
    document.body.style.overflow = 'hidden';
    
    return () => {
      // Разблокируем скролл при закрытии
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      await historyService.syncFromBackend();
      if (cancelled) return;
      const historyData = historyService.getHistory();
      setHistory(historyData);

      const formatted = {};
      for (const item of historyData) {
        if (!item.title || item.title === item.url) {
          formatted[item.url] = await formatUrl(item.url);
        }
      }
      if (!cancelled) setFormattedTitles(formatted);
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const formatUrl = async (url) => {
    const path = normalizeBibleHistoryPath(url);
    const parts = path.replace(/^\//, '').split('/').filter(Boolean);
    const translation = parts[0] || 'kjv';

    if (parts.length === 1) {
      return t('home');
    }
    if (parts.length >= 2) {
      const bookId = parseInt(parts[1]);
      if (!isNaN(bookId)) {
        try {
          const books = await bibleService.getBooks(translation);
          const book = books.find(b => b.id === bookId);
          if (book) {
            if (parts.length === 2) {
              return book.name;
            } else if (parts.length === 3) {
              return `${book.name} ${parts[2]}`;
            } else if (parts.length === 4) {
              return `${book.name} ${parts[2]}:${parts[3]}`;
            }
          }
        } catch (error) {
          console.error('Error loading books for formatUrl:', error);
        }
      }
      if (parts.length === 2) {
        return `${t('bookWord')} ${parts[1]}`;
      } else if (parts.length === 3) {
        return `${t('bookWord')} ${parts[1]}, ${t('chapterWord')} ${parts[2]}`;
      } else if (parts.length === 4) {
        return `${t('bookWord')} ${parts[1]}, ${t('chapterWord')} ${parts[2]}, ${t('verseWord')} ${parts[3]}`;
      }
    }

    return url;
  };

  useEffect(() => {
    // Закрытие по клику вне модального окна
    const handleClickOutside = (e) => {
      if (e.target.classList.contains('history-modal')) {
        onClose();
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [onClose]);

  const handleNavigate = (url) => {
    navigate(url);
    onClose();
  };

  const handleClearHistory = () => {
    if (window.confirm(t('historyClearConfirm'))) {
      historyService.clearHistory();
      setHistory([]);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) { // Меньше минуты
      return t('timeJustNow');
    } else if (diff < 3600000) { // Меньше часа
      const minutes = Math.floor(diff / 60000);
      return t('timeMinutesAgo').replace('{count}', String(minutes));
    } else if (diff < 86400000) { // Меньше суток
      const hours = Math.floor(diff / 3600000);
      return t('timeHoursAgo').replace('{count}', String(hours));
    } else {
      return formatDateTime(timestamp);
    }
  };

  return (
    <div className="history-modal" onClick={onClose}>
      <div className="history-content" onClick={(e) => e.stopPropagation()}>
        <div className="bible-modal-sheet-handle" aria-hidden="true" />
        <div className="chapter-selector-content-header">
          <h2 className="text-base font-semibold text-slate-700">{t('history')}</h2>
          <div className="flex items-center gap-2">
            {history.length > 0 && (
              <button
                type="button"
                onClick={handleClearHistory}
                className="text-xs text-slate-500 hover:text-slate-700 px-2 py-1 rounded transition-colors font-medium"
              >
                {t('historyClear')}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 flex-shrink-0 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors text-lg font-light"
              aria-label={t('close')}
            >
              ×
            </button>
          </div>
        </div>

        <div className="chapter-selector-scroll">
          {history.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-slate-500">{t('historyEmpty')}</p>
            </div>
          ) : (
            <div className="history-list">
              {history.map((item, idx) => (
                <button
                  key={`${item.url}-${item.timestamp ?? idx}`}
                  type="button"
                  onClick={() => handleNavigate(item.url)}
                  className="history-item"
                >
                  <div className="flex-1 min-w-0 text-left">
                    <p className="history-item-title">
                      {item.title && item.title !== item.url
                        ? item.title
                        : formattedTitles[item.url] || item.url}
                    </p>
                    <p className="history-item-url">{item.url}</p>
                  </div>
                  <div className="history-item-time">
                    {formatTime(item.timestamp)}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

